/**
 * ConditionsRenderer - Renderer for trigger condition expressions
 *
 * Allows adding multiple condition expressions that must evaluate to true
 * for the trigger to fire.
 */

import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Field,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';

import {
  settingContainerStyle,
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelContainerStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: settingContainerStyle,
  header: settingHeaderStyle,
  labelContainer: settingLabelContainerStyle,
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  conditionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  conditionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  conditionInput: {
    flex: 1,
  },
  deleteButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  emptyState: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: tokens.spacingVerticalS,
  },
  hint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
});

export interface ConditionsRendererProps extends SettingRendererProps<string[]> {
  label?: string;
  description?: string;
}

/**
 * ConditionsRenderer component for trigger conditions
 */
export const ConditionsRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Trigger Conditions',
  description = 'Conditions that must be true for the trigger to fire',
}: ConditionsRendererProps) => {
  const styles = useStyles();
  const [newCondition, setNewCondition] = useState('');

  if (!setting.isSupported) {
    return null;
  }

  const conditions = setting.value ?? [];

  const handleAddCondition = () => {
    if (newCondition.trim()) {
      onChange([...conditions, newCondition.trim()]);
      setNewCondition('');
    }
  };

  const handleRemoveCondition = (index: number) => {
    const updated = conditions.filter((_, i) => i !== index);
    onChange(updated.length > 0 ? updated : undefined);
  };

  const handleUpdateCondition = (index: number, value: string) => {
    const updated = [...conditions];
    updated[index] = value;
    onChange(updated);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddCondition();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.labelContainer}>
          <Text className={styles.label}>{label}</Text>
          <Text className={styles.description}>{description}</Text>
        </div>
      </div>

      <div className={styles.conditionsList}>
        {conditions.length === 0 ? (
          <Text className={styles.emptyState}>
            No conditions configured. Trigger will fire for all events.
          </Text>
        ) : (
          conditions.map((condition, index) => (
            <div key={index} className={styles.conditionRow}>
              <Input
                className={styles.conditionInput}
                value={condition}
                onChange={(e) => handleUpdateCondition(index, e.target.value)}
                placeholder="${triggerBody()?.['status'] == 'active'}"
                disabled={readOnly}
                aria-label={`Condition ${index + 1}`}
              />
              {!readOnly && (
                <Button
                  className={styles.deleteButton}
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => handleRemoveCondition(index)}
                  aria-label={`Remove condition ${index + 1}`}
                />
              )}
            </div>
          ))
        )}

        {!readOnly && (
          <>
            <div className={styles.conditionRow}>
              <Field className={styles.conditionInput}>
                <Input
                  value={newCondition}
                  onChange={(e) => setNewCondition(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add new condition expression..."
                  aria-label="New condition"
                />
              </Field>
              <Button
                appearance="subtle"
                icon={<AddRegular />}
                onClick={handleAddCondition}
                disabled={!newCondition.trim()}
                aria-label="Add condition"
              />
            </div>
            <Text className={styles.hint}>
              {"Use JavaScript expressions like ${a == b}, ${a.includes(b)}, or ${a > b}."}
            </Text>
          </>
        )}
      </div>
    </div>
  );
};

ConditionsRenderer.displayName = 'ConditionsRenderer';

registerSettingRenderer('conditions', ConditionsRenderer as React.ComponentType<SettingRendererProps<unknown>>);
